import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Clock, Users as UsersIcon, Briefcase, Calendar, Trash2, Pencil, X, Check } from 'lucide-react';

const GREEN = '#0F6E56';

function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

function formatDateBr(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function firstDayOfMonthIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`;
}

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function TimeTracking({ user }) {
  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';
  const isClient = (user?.role || '').toUpperCase() === 'CLIENT';

  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);

  const [dateFrom, setDateFrom] = useState(firstDayOfMonthIso());
  const [dateTo, setDateTo] = useState(todayIso());
  const [projectFilter, setProjectFilter] = useState('__all__');
  const [userFilter, setUserFilter] = useState('__all__');

  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  // Edit-inline state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ entry_date: '', duration_minutes: 0, description: '' });

  useEffect(() => {
    if (isClient) return;
    (async () => {
      try {
        const [p, u] = await Promise.all([
          api.get('/projects'),
          isAdmin ? api.get('/users') : Promise.resolve({ data: [] }),
        ]);
        setProjects(p.data || []);
        setUsers((u.data || []).filter((usr) =>
          ['ADMIN', 'CONSULTANT'].includes((usr.role || '').toUpperCase()),
        ));
      } catch (e) {
        // silent
      }
    })();
  }, [isAdmin, isClient]);

  const loadData = async () => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (projectFilter && projectFilter !== '__all__') params.project_id = projectFilter;
    if (isAdmin && userFilter && userFilter !== '__all__') params.user_id = userFilter;
    try {
      const [rep, list] = await Promise.all([
        api.get('/time-entries/summary/report', { params }),
        api.get('/time-entries', { params }),
      ]);
      setSummary(rep.data);
      setEntries(list.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isClient) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, projectFilter, userFilter]);

  const canModify = (entry) => isAdmin || entry.user_id === user?.user_id;

  const startEdit = (e) => {
    setEditingId(e.entry_id);
    setEditForm({
      entry_date: e.entry_date,
      duration_minutes: e.duration_minutes,
      description: e.description || '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ entry_date: '', duration_minutes: 0, description: '' });
  };

  const saveEdit = async () => {
    if (!editForm.entry_date) return toast.error('Data obrigatória.');
    const dur = parseInt(editForm.duration_minutes || 0, 10);
    if (!dur || dur <= 0) return toast.error('Duração deve ser maior que zero.');
    try {
      const { data } = await api.put(`/time-entries/${editingId}`, {
        entry_date: editForm.entry_date,
        duration_minutes: dur,
        description: (editForm.description || '').trim() || null,
      });
      setEntries((prev) => prev.map((e) => (e.entry_id === editingId ? data : e)));
      toast.success('Registro atualizado');
      cancelEdit();
      loadData(); // refresh summary
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Excluir este registro?')) return;
    try {
      await api.delete(`/time-entries/${id}`);
      setEntries((prev) => prev.filter((e) => e.entry_id !== id));
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const filteredCount = entries.length;

  const totalMinutes = summary?.total_minutes || 0;
  const totalEntries = summary?.total_entries || 0;

  if (isClient) {
    return (
      <Layout user={user}>
        <Card className="p-6 text-center text-gray-600">
          Registro de horas é interno da equipe.
        </Card>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Clock className="w-6 h-6" style={{ color: GREEN }} />
          <h1 className="text-2xl font-semibold text-gray-900" data-testid="time-tracking-title">
            Registro de horas
          </h1>
        </div>

        {/* Filters */}
        <Card className="p-4 border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>De</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="filter-date-from"
              />
            </div>
            <div>
              <Label>Até</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="filter-date-to"
              />
            </div>
            <div>
              <Label>Projeto</Label>
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger data-testid="filter-project"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos os projetos</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAdmin && (
              <div>
                <Label>Usuário</Label>
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger data-testid="filter-user"><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.full_name || u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="p-4 border border-gray-200" data-testid="kpi-total-hours">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5" style={{ color: GREEN }} />
              <div>
                <p className="text-xs text-gray-500">Total no período</p>
                <p className="text-2xl font-semibold text-gray-900">{formatDuration(totalMinutes)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5" style={{ color: GREEN }} />
              <div>
                <p className="text-xs text-gray-500">Lançamentos</p>
                <p className="text-2xl font-semibold text-gray-900">{totalEntries}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <Briefcase className="w-5 h-5" style={{ color: GREEN }} />
              <div>
                <p className="text-xs text-gray-500">Projetos com registros</p>
                <p className="text-2xl font-semibold text-gray-900">{summary?.by_project?.length || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* By project */}
        <Card className="p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Briefcase className="w-4 h-4" style={{ color: GREEN }} /> Por projeto
          </h2>
          {(!summary?.by_project || summary.by_project.length === 0) ? (
            <p className="text-sm text-gray-500 italic py-4">Nenhum registro no período.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-by-project">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                    <th className="py-2">Projeto</th>
                    <th className="py-2 text-right">Lançamentos</th>
                    <th className="py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.by_project.map((r) => (
                    <tr key={r.project_id}>
                      <td className="py-2 text-gray-900">{r.project_name}</td>
                      <td className="py-2 text-right text-gray-600">{r.entry_count}</td>
                      <td className="py-2 text-right font-medium" style={{ color: GREEN }}>
                        {formatDuration(r.total_minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* By user (admin only) */}
        {isAdmin && (
          <Card className="p-4 border border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <UsersIcon className="w-4 h-4" style={{ color: GREEN }} /> Por usuário
            </h2>
            {(!summary?.by_user || summary.by_user.length === 0) ? (
              <p className="text-sm text-gray-500 italic py-4">Nenhum registro no período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="table-by-user">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                      <th className="py-2">Usuário</th>
                      <th className="py-2 text-right">Lançamentos</th>
                      <th className="py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {summary.by_user.map((r) => (
                      <tr key={r.user_id}>
                        <td className="py-2 text-gray-900">{r.user_name}</td>
                        <td className="py-2 text-right text-gray-600">{r.entry_count}</td>
                        <td className="py-2 text-right font-medium" style={{ color: GREEN }}>
                          {formatDuration(r.total_minutes)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Entries list */}
        <Card className="p-4 border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" style={{ color: GREEN }} /> Lançamentos ({filteredCount})
          </h2>
          {loading ? (
            <p className="text-sm text-gray-500 py-4">Carregando...</p>
          ) : entries.length === 0 ? (
            <p className="text-sm text-gray-500 italic py-4">
              Nenhum lançamento no período. Registre horas em uma tarefa nos detalhes do projeto.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-entries">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                    <th className="py-2">Data</th>
                    <th className="py-2">Projeto</th>
                    <th className="py-2">Tarefa</th>
                    {isAdmin && <th className="py-2">Usuário</th>}
                    <th className="py-2">Descrição</th>
                    <th className="py-2 text-right">Duração</th>
                    <th className="py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((e) => {
                    const isEditing = editingId === e.entry_id;
                    return (
                      <tr key={e.entry_id} data-testid={`entry-row-${e.entry_id}`}>
                        <td className="py-2 whitespace-nowrap">
                          {isEditing ? (
                            <Input
                              type="date"
                              value={editForm.entry_date}
                              onChange={(ev) => setEditForm({ ...editForm, entry_date: ev.target.value })}
                              className="h-8 text-xs"
                            />
                          ) : formatDateBr(e.entry_date)}
                        </td>
                        <td className="py-2 text-gray-700">{e.project_name || '—'}</td>
                        <td className="py-2 text-gray-700">{e.task_name || '—'}</td>
                        {isAdmin && <td className="py-2 text-gray-700">{e.user_name || '—'}</td>}
                        <td className="py-2 text-gray-600 max-w-xs">
                          {isEditing ? (
                            <Input
                              value={editForm.description}
                              onChange={(ev) => setEditForm({ ...editForm, description: ev.target.value })}
                              className="h-8 text-xs"
                              maxLength={500}
                            />
                          ) : (
                            <span className="line-clamp-2">{e.description || '—'}</span>
                          )}
                        </td>
                        <td className="py-2 text-right font-medium" style={{ color: GREEN }}>
                          {isEditing ? (
                            <Input
                              type="number"
                              min="1"
                              max={24 * 60}
                              value={editForm.duration_minutes}
                              onChange={(ev) => setEditForm({ ...editForm, duration_minutes: ev.target.value })}
                              className="h-8 text-xs w-20 text-right ml-auto"
                              title="Duração em minutos"
                            />
                          ) : formatDuration(e.duration_minutes)}
                        </td>
                        <td className="py-2 text-right">
                          {canModify(e) && (
                            <div className="flex justify-end gap-1">
                              {isEditing ? (
                                <>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 px-2"
                                    onClick={saveEdit}
                                    data-testid={`save-edit-${e.entry_id}`}
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 px-2"
                                    onClick={cancelEdit}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 px-2"
                                    onClick={() => startEdit(e)}
                                    data-testid={`edit-entry-${e.entry_id}`}
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </Button>
                                  <Button
                                    size="sm" variant="outline"
                                    className="h-7 px-2 text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => deleteEntry(e.entry_id)}
                                    data-testid={`delete-entry-${e.entry_id}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="text-xs text-gray-500">
          Registros são criados na aba <b>Fases</b> de cada projeto, através do botão <Clock className="inline w-3 h-3" /> em cada tarefa.
        </p>
      </div>
    </Layout>
  );
}

export default TimeTracking;
